import { useEffect, useRef } from 'react';
import { useIsIdle } from '@/utils/idle-detector';
import { isNotMidTask } from '@/utils/reload-safety';

/** A visible-but-idle tab auto-updates after this long with no user activity. */
export const AUTO_UPDATE_IDLE_MS = 30 * 60 * 1000; // 30 minutes
/** How often to re-check safety while an update is pending (catches a modal
 *  closing or an in-flight save finishing on an otherwise-idle tab). */
const SAFETY_RECHECK_MS = 30 * 1000;

/**
 * Auto-applies a waiting service-worker update on tabs where it is safe to
 * reload, so long-lived tabs (EOC wall displays, backgrounded tabs) don't drift
 * onto a stale frontend that no human is around to click "Update Now" on.
 *
 * Safe = (tab hidden OR idle >= 30 min) AND not mid-task. Actively-used tabs
 * fall through to the existing manual "Update Now" banner instead. No-op until
 * `isUpdateAvailable` is true, and applies at most once per page load.
 *
 * @param isUpdateAvailable from `useServiceWorker()`
 * @param applyUpdate       from `useServiceWorker()` (posts SKIP_WAITING → reload)
 */
export function useAutoUpdate(isUpdateAvailable: boolean, applyUpdate: () => void): void {
  const isIdle = useIsIdle(AUTO_UPDATE_IDLE_MS);
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!isUpdateAvailable || appliedRef.current) return;
    if (typeof document === 'undefined') return;

    const tryApply = () => {
      if (appliedRef.current) return;
      const hidden = document.visibilityState === 'hidden';
      if ((hidden || isIdle) && isNotMidTask()) {
        appliedRef.current = true;
        applyUpdate();
      }
    };

    tryApply(); // evaluate immediately on update / idle / visibility change
    document.addEventListener('visibilitychange', tryApply);
    const interval = setInterval(tryApply, SAFETY_RECHECK_MS);

    return () => {
      document.removeEventListener('visibilitychange', tryApply);
      clearInterval(interval);
    };
  }, [isUpdateAvailable, isIdle, applyUpdate]);
}
